// RecipeEditor.jsx
import { Box, Button, Container, FormControlLabel, Switch, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { RecipeContext } from "../../contexts/RecipeContext";
import { UserContext } from "../../contexts/UserContext";
import styles from "../../css/RecipeEditor.module.css";
import { useRecipeData } from "../../hooks/RecipeHooks/useRecipeData";
import { createUpdatedRecipe, validateFields } from "../../utils/recipeFormUtils";
import LoadingSpinner from "../Common/LoadingSpinner";
import RecipeComponents from "./RecipeComponents";
import RecipeDataImporter from "./RecipeDataImporter";
import RecipeForm from "./RecipeForm";

const RecipeEditor = ({ isEditMode = false }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { recipes, addNewRecipe, copyRecipe, updateExistingRecipe } = useContext(RecipeContext);
  const { user } = useContext(UserContext);
  const { existingRecipe } = useRecipeData(recipes, id, isEditMode);

  const userId = user?.uid || null;
  const isOwner = existingRecipe?.createdBy === userId;
  const isCopyMode = isEditMode && !isOwner;

  const [formState, setFormState] = useState({
    title: "",
    description: "",
    servings: "",
    prepTime: "",
    cookTime: "",
    author: "",
    ingredients: [],
    instructions: [],
    images: [],
    tags: [],
    message: "",
    errors: {},
    loading: false,
    isPublic: false,
  });

  useEffect(() => {
    if (isEditMode && existingRecipe) {
      setFormState((prev) => ({
        ...prev,
        title: existingRecipe.title || "",
        description: existingRecipe.description || "",
        servings: existingRecipe.servings || "",
        prepTime: existingRecipe.prepTime || "",
        cookTime: existingRecipe.cookTime || "",
        author: existingRecipe.author || "",
        ingredients: existingRecipe.ingredients || [],
        instructions: existingRecipe.instructions || [],
        images: existingRecipe.images || [],
        tags: existingRecipe.tags || [],
      }));
    }
  }, [isEditMode, existingRecipe]);

  useEffect(() => {
    const appName = t("appTitle");
    document.title = isEditMode && existingRecipe ? `${t("recipeTitle.edit")} ${existingRecipe.title} - ${appName}` : `${t("recipeTitle.addNew")} - ${appName}`;
  }, [isEditMode, existingRecipe, t]);

  const saveRecipe = async () => {
    const isValid = validateFields(formState, setFormState, t);
    if (!isValid) {
      console.warn("Validation failed. Recipe not saved.");
      return;
    }

    setFormState((prev) => ({ ...prev, loading: true }));

    try {
      const formValues = {
        title: formState.title,
        description: formState.description,
        servings: formState.servings,
        prepTime: formState.prepTime,
        cookTime: formState.cookTime,
        author: formState.author,
      };

      const baseRecipeData = createUpdatedRecipe(
        formValues,
        isEditMode && isOwner,
        isEditMode && isOwner ? existingRecipe : null,
        formState.ingredients,
        formState.instructions,
        formState.images,
        formState.tags,
        recipes,
      );

      let savedRecipe;
      if (!isEditMode) {
        savedRecipe = await addNewRecipe({ ...baseRecipeData, createdBy: userId ?? null, isPublic: formState.isPublic, createdAt: new Date().toISOString() });
        setFormState((prev) => ({ ...prev, message: t("recipeSavedSuccess") }));
      } else if (isOwner) {
        savedRecipe = await updateExistingRecipe({ ...baseRecipeData, isPublic: formState.isPublic, updatedAt: new Date().toISOString() });
        setFormState((prev) => ({ ...prev, message: t("recipeSavedSuccess") }));
      } else {
        savedRecipe = await copyRecipe(existingRecipe, userId);
        setFormState((prev) => ({ ...prev, message: t("recipeCopiedSuccess") }));
      }

      navigate(`/recipe/${savedRecipe.id}`);
    } catch (error) {
      console.error("Error saving recipe:", error);
      setFormState((prev) => ({ ...prev, message: t("recipeSaveError") }));
    } finally {
      setFormState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleJsonParsed = (parsedRecipe) => {
    setFormState((prev) => ({
      ...prev,
      title: parsedRecipe.title || "",
      description: parsedRecipe.description || "",
      servings: parsedRecipe.servings || "",
      prepTime: parsedRecipe.prepTime || "",
      cookTime: parsedRecipe.cookTime || "",
      author: parsedRecipe.author || "",
      ingredients: parsedRecipe.ingredients || [],
      instructions: parsedRecipe.instructions || [],
      images: parsedRecipe.images || [],
      tags: parsedRecipe.tags || [],
      message: "JSON successfully loaded into fields.",
    }));
  };

  if (formState.loading) return <LoadingSpinner />;

  return (
    <Container className={styles.noPaddingMobile}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap">
        <Typography variant="h4" component="h2" gutterBottom sx={{ flexGrow: 1 }}>
          {isEditMode ? (isOwner ? t("recipeTitle.edit") : t("recipeTitle.createCopy")) : t("recipeTitle.addNew")}
        </Typography>
        <Box display="flex" alignItems="center">
          <Link to={existingRecipe ? `/recipe/${existingRecipe.id}` : `/recipes`} style={{ textDecoration: "none" }}>
            <Button size="small" color="primary" sx={{ ml: 2 }}>
              {t("backButton")}
            </Button>
          </Link>
          <Button onClick={saveRecipe} variant="contained" color="primary" sx={{ ml: 2 }} >
            {isCopyMode ? t("saveAsCopyButtonLabel") : t("saveButtonLabel")}
          </Button>
        </Box>
      </Box>
      {isEditMode && existingRecipe && (
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontStyle: "italic" }}>
          {isOwner ? t("editingOwnRecipe") : t("creatingCopy")}
        </Typography>
      )}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <FormControlLabel
          control={<Switch checked={formState.isPublic} onChange={(e) => !isCopyMode && setFormState((prev) => ({ ...prev, isPublic: e.target.checked }))} color="primary"  disabled={!user || isCopyMode} />}
          label={formState.isPublic ? t("publicRecipe") : t("privateRecipe")}
        />
      </Box>
      {/* <RecipeDataImporter formState={formState} setFormState={setFormState} onJsonParsed={handleJsonParsed} /> */}
      <RecipeForm values={formState} setValues={setFormState} errors={formState.errors} />
      <RecipeComponents {...formState} updateFormState={setFormState} />
    </Container>
  );
};

export default RecipeEditor;
